import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DndContext, useDraggable, useDroppable, type DragEndEvent } from '@dnd-kit/core'
import toast from 'react-hot-toast'
import { assegnazioniCantiereApi, tecniciApi, commesseApi, cantieriApi, mezziApi } from '../services/api'

interface AssegnazioneTimelineProps {
  data: string
  onDataChange: (d: string) => void
}

interface AssegnazioneTL {
  id: number
  commessa_id: number
  cantiere_id: number
  tecnico_id: number
  data_inizio: string
  ora_inizio?: string | null
  mezzo_id?: number | null
  mezzo_targa?: string | null
  commessa_nome?: string | null
  cantiere_nome?: string | null
  tecnico_nome?: string | null
  notifica_inviata: boolean
}

interface TecnicoTL { id: number; nome: string; cognome: string }
interface CommessaTL { id: number; numero_commessa: string; nome: string }
interface CantiereTL { id: number; commessa_id: number; nome: string }
interface MezzoTL { id: number; targa: string; marca?: string; modello?: string }

const SLOT_ORARI = Array.from({ length: 25 }, (_, i) => {
  const ora = 7 + Math.floor(i / 2)
  const minuti = i % 2 === 0 ? '00' : '30'
  return `${String(ora).padStart(2, '0')}:${minuti}`
})

function TecnicoAvatar({ tecnicoId, nome }: { tecnicoId: number; nome: string }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `tec-${tecnicoId}` })
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="badge cursor-grab select-none">
      {nome}
    </div>
  )
}

function SlotCell({ tecnicoId, slot, assegnazione }: { tecnicoId: number; slot: string; assegnazione?: AssegnazioneTL }) {
  const { setNodeRef, isOver } = useDroppable({ id: `slot-${tecnicoId}-${slot}` })
  return (
    <div ref={setNodeRef} className={`h-10 border-r border-[var(--border)] flex items-center justify-center ${isOver ? 'bg-blue-100' : ''}`}>
      {assegnazione && <AssegnazioneBlock assegnazione={assegnazione} />}
    </div>
  )
}

function AssegnazioneBlock({ assegnazione }: { assegnazione: AssegnazioneTL }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `asseg-${assegnazione.id}` })
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 } : undefined
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      title={`${assegnazione.tecnico_nome} — ${assegnazione.commessa_nome}`}
      className={`badge cursor-grab select-none text-[10px] px-1 py-0.5 truncate max-w-[64px] ${assegnazione.notifica_inviata ? 'opacity-70' : ''}`}
    >
      {assegnazione.commessa_nome || `#${assegnazione.commessa_id}`}
    </div>
  )
}

export default function AssegnazioneTimeline({ data, onDataChange }: AssegnazioneTimelineProps) {
  const queryClient = useQueryClient()
  const [popup, setPopup] = useState<{ tecnicoId: number; slot: string; assegnazioneId?: number } | null>(null)
  const [popupCommessaId, setPopupCommessaId] = useState<number | ''>('')
  const [popupCantiereId, setPopupCantiereId] = useState<number | ''>('')
  const [popupMezzoId, setPopupMezzoId] = useState<number | ''>('')

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-attivi-timeline'],
    queryFn: () => tecniciApi.list({ per_page: 200, is_active: true }).then(r => r.data.items as TecnicoTL[]),
  })
  const tecnici = tecniciData || []

  const { data: assegnazioniData, refetch } = useQuery({
    queryKey: ['assegnazioni-timeline', data],
    queryFn: () => assegnazioniCantiereApi.list({ data_da: data, data_a: data }).then(r => r.data as AssegnazioneTL[]),
  })
  const assegnazioni = assegnazioniData || []

  const { data: commesseData } = useQuery({
    queryKey: ['commesse-timeline'],
    queryFn: () => commesseApi.list().then(r => r.data as CommessaTL[]),
  })
  const commesse = commesseData || []

  const { data: cantieriAllData } = useQuery({
    queryKey: ['cantieri-timeline'],
    queryFn: () => cantieriApi.list().then(r => r.data as CantiereTL[]),
  })
  const cantieriAll = cantieriAllData || []
  const cantieri = popupCommessaId ? cantieriAll.filter((c) => c.commessa_id === popupCommessaId) : []

  const { data: mezziData } = useQuery({
    queryKey: ['mezzi-attivi-timeline'],
    queryFn: () => mezziApi.list(true).then(r => r.data as MezzoTL[]),
  })
  const mezzi = mezziData || []

  const createMutation = useMutation({
    mutationFn: (payload: any) => assegnazioniCantiereApi.create(payload),
    onSuccess: () => {
      toast.success('Assegnazione creata')
      setPopup(null)
      refetch()
      queryClient.invalidateQueries({ queryKey: ['assegnazioni'] })
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore creazione'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => assegnazioniCantiereApi.update(id, payload),
    onSuccess: () => {
      toast.success('Orario aggiornato')
      refetch()
      queryClient.invalidateQueries({ queryKey: ['assegnazioni'] })
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore aggiornamento'),
  })

  const bulkNotificaMutation = useMutation({
    mutationFn: () => {
      const idsNonNotificati = assegnazioni.filter((a) => !a.notifica_inviata).map((a) => a.id)
      return assegnazioniCantiereApi.inviaNotificheBulk(idsNonNotificati)
    },
    onSuccess: (res: any) => {
      toast.success(`Notifiche inviate: ${res.data.inviate}, errori: ${res.data.errori.length}`)
      refetch()
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Errore invio notifiche'),
  })

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)

    if (activeId.startsWith('tec-')) {
      // drag dell'avatar tecnico -> nuova assegnazione
      const tecnicoId = parseInt(activeId.replace('tec-', ''), 10)
      const slot = overId.replace(`slot-${tecnicoId}-`, '')
      // Controllo sovrapposizione: stesso tecnico, stesso giorno, stesso slot
      const esistente = assegnazioni.find(a => a.tecnico_id === tecnicoId && a.ora_inizio === slot)
      if (esistente) {
        toast.error(`Sovrapposizione: ${esistente.commessa_nome || '#'+esistente.commessa_id} già assegnato alle ${slot}`)
        return
      }
      setPopup({ tecnicoId, slot })
      setPopupCommessaId('')
      setPopupCantiereId('')
      setPopupMezzoId('')
    } else if (activeId.startsWith('asseg-')) {
      // drag di un blocco assegnazione esistente -> aggiorna orario e/o tecnico
      const assegnazioneId = parseInt(activeId.replace('asseg-', ''), 10)
      const assegnazione = assegnazioni.find((a) => a.id === assegnazioneId)
      if (!assegnazione) return
      // Estrai tecnicoId e slot dal droppable
      const match = overId.match(/^slot-(\d+)-(.+)$/)
      if (!match) {
        toast.error('Destinazione non valida')
        return
      }
      const nuovoTecnicoId = parseInt(match[1], 10)
      const nuovoSlot = match[2]
      if (!SLOT_ORARI.includes(nuovoSlot)) {
        toast.error('Slot orario non valido')
        return
      }
      // Controllo sovrapposizione sul nuovo tecnico/slot
      const esistente = assegnazioni.find(
        a => a.tecnico_id === nuovoTecnicoId && a.ora_inizio === nuovoSlot && a.id !== assegnazioneId
      )
      if (esistente) {
        toast.error(`Sovrapposizione: ${esistente.commessa_nome || '#'+esistente.commessa_id} già assegnato alle ${nuovoSlot}`)
        return
      }
      const payload: Record<string, any> = { ora_inizio: nuovoSlot }
      if (nuovoTecnicoId !== assegnazione.tecnico_id) {
        payload.tecnico_id = nuovoTecnicoId
      }
      updateMutation.mutate({ id: assegnazioneId, payload })
    }
  }

  function confermaPopup() {
    if (!popup || !popupCommessaId || !popupCantiereId) {
      toast.error('Seleziona commessa e cantiere')
      return
    }
    createMutation.mutate({
      commessa_id: popupCommessaId,
      cantiere_id: popupCantiereId,
      tecnico_id: popup.tecnicoId,
      data_inizio: data,
      ora_inizio: popup.slot,
      mezzo_id: popupMezzoId || undefined,
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <input type="date" className="input w-auto" value={data} onChange={(e) => onDataChange(e.target.value)} />
        <button className="btn-primary" onClick={() => bulkNotificaMutation.mutate()} disabled={bulkNotificaMutation.isPending}>
          Invia notifiche
        </button>
      </div>

      <DndContext onDragEnd={handleDragEnd}>
        <div className="flex gap-2 mb-3 flex-wrap">
          {tecnici.map((t) => (
            <TecnicoAvatar key={t.id} tecnicoId={t.id} nome={`${t.nome} ${t.cognome}`} />
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-1">Tecnico</th>
                {SLOT_ORARI.map((s) => <th key={s} className="p-1 text-xs">{s}</th>)}
              </tr>
            </thead>
            <tbody>
              {tecnici.map((t) => {
                const assegnazioniTecnico = assegnazioni.filter((a) => a.tecnico_id === t.id)
                return (
                  <tr key={t.id}>
                    <td className="p-1 whitespace-nowrap">{t.nome} {t.cognome}</td>
                    {SLOT_ORARI.map((s) => {
                      const assegnazioneSlot = assegnazioniTecnico.find((a) => a.ora_inizio === s)
                      return <td key={s}><SlotCell tecnicoId={t.id} slot={s} assegnazione={assegnazioneSlot} /></td>
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </DndContext>

      <div className="mt-4 flex flex-wrap gap-2">
        {assegnazioni.map((a) => (
          <div key={a.id} className="badge">
            {a.tecnico_nome} — {a.ora_inizio} — {a.commessa_nome} {a.mezzo_targa ? `— ${a.mezzo_targa}` : ''} {a.notifica_inviata ? '✓ inviata' : ''}
          </div>
        ))}
      </div>

      {popup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="card w-full max-w-md p-6">
            <h3 className="font-semibold mb-3">Assegna {popup.slot}</h3>
            <label className="label">Commessa</label>
            <select className="select mb-3" value={popupCommessaId} onChange={(e) => setPopupCommessaId(Number(e.target.value))}>
              <option value="">Seleziona...</option>
              {commesse.map((c) => <option key={c.id} value={c.id}>{c.numero_commessa} — {c.nome}</option>)}
            </select>
            <label className="label">Cantiere</label>
            <select className="select mb-3" value={popupCantiereId} onChange={(e) => setPopupCantiereId(Number(e.target.value))} disabled={!popupCommessaId}>
              <option value="">Seleziona...</option>
              {cantieri.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            <label className="label">Mezzo (opzionale)</label>
            <select className="select mb-4" value={popupMezzoId} onChange={(e) => setPopupMezzoId(Number(e.target.value) || '')}>
              <option value="">Nessuno</option>
              {mezzi.map((m) => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setPopup(null)}>Annulla</button>
              <button className="btn-primary" onClick={confermaPopup} disabled={createMutation.isPending}>Conferma</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
